import { Message } from "google-protobuf";
import { findKey, toNumber } from "lodash";
import * as methods from "../../constants/methods";
import { MessageResponse, protoResponsesTable } from "../../constants/protoTable";
import handlerTable from "../../handlers";
import { ErrorResponse } from "../../proto/Error_pb";
import store from "../../store";
import { setConnectionStatus } from "../../store/connection/actions";
import { ConnectionStatusType } from "../../store/connection/types";
import ClientError from "../Error/ClientError";
import ServerError from "../Error/ServerError";
import { arrayBufferToString, isDevMod, msSleep, stringToUint8Array } from "../utils";
import Api, { setServerTime } from "./index";
import RequestWrapper from "./RequestWrapper";
import HandleErrorResponse from "../../handlers/HandleErrorResponse";

const forge = require("@web/node-forge");

const singletonEnforcer = Symbol();

export default class Client {
  static _clientSingleton: Client;
  static token: string;
  private _socket?: WebSocket;
  private _keepAlive: boolean = true;
  private _secure: boolean = false;
  private _loggedIn: boolean = false;
  private _symmetricKey?: string;
  private _heartbeatInterval?: any;
  private _symmetricMethod?: string[];
  private _symmetricIvSize?: number;

  private constructor(enforcer: Symbol) {
    //
    if (enforcer !== singletonEnforcer) {
      throw new Error("Cannot construct singleton");
    }
    this._init();
  }

  /**
   * Get Client instance
   * @returns {Client}
   */
  static get instance() {
    if (!this._clientSingleton) {
      this._clientSingleton = new Client(singletonEnforcer);
    }
    return this._clientSingleton;
  }

  get isOnline() {
    return this._socket && this._socket.readyState === WebSocket.OPEN;
  }

  get isSecure() {
    return this.isOnline && this._secure;
  }

  get isLoggedIn() {
    return this.isOnline && this._loggedIn;
  }

  set keepAlive(value: boolean) {
    this._keepAlive = value;
  }

  set secure(value: boolean) {
    this._secure = value;
  }

  set loggedIn(value: boolean) {
    this._loggedIn = value;
  }

  set symmetricKey(value: string) {
    this._symmetricKey = value;
  }

  get getSymmetricKey() {
    return this._symmetricKey;
  }

  set heartbeatInterval(value: any) {
    this._heartbeatInterval = value;
  }

  set symmetricMethod(value: string) {
    this._symmetricMethod = value.split("-");
  }

  set symmetricIvSize(value: number) {
    this._symmetricIvSize = value;
  }

  async _init() {
    try {
      await this._connect();
    } catch (e) {}
  }

  _connect(): Promise<boolean> {
    //
    console.log("#>>> _connect new Socket");

    if (!this._keepAlive) {
      return Promise.reject();
    }

    this.secure = false;
    this.loggedIn = false;

    return new Promise((resolve: Function, reject: Function) => {
      this._socket = new WebSocket(process.env.REACT_APP_WEBSOCKET_ENDPOINT!);
      this._socket.binaryType = "arraybuffer";
      this._socket.onopen = () => {
        //
        console.log("#primarySocket Open", this._socket);
        store.dispatch(setConnectionStatus(ConnectionStatusType.CONNECTED));
        resolve(true);
      };

      this._socket.onmessage = this._onMessage;

      this._socket.onerror = (error) => {
        console.error("[Client] ON ERROR", error);
        reject(error);
      };

      this._socket.onclose = this._onClose.bind(this);
    });
  }

  _onMessage = (ev: MessageEvent): any => {
    try {
      let data: ArrayBuffer = ev.data;

      if (this._secure) {
        data = this._decrypt(data).buffer;
      }
      const responseActionId = new DataView(data, 0, 2).getUint16(0, true);
      // console.log("new DataView Error,", `Data ${responseActionId}`);
      this.__processMessage(responseActionId as keyof typeof protoResponsesTable, new Uint8Array(data, 2), true);
    } catch (e) {
      console.error(`#WS _onMessage`, e);
    }
  };

  /**
   * @param {number} responseActionId
   * @param {Uint8Array} payload
   * @param {boolean} isFromServer
   */
  __processMessage(responseActionId: keyof typeof protoResponsesTable, payload: Uint8Array, isFromServer: boolean) {
    if (protoResponsesTable.hasOwnProperty(responseActionId)) {
      const responseProto: MessageResponse = protoResponsesTable[responseActionId].deserializeBinary(payload);

      if (window.Config.debugMode || isDevMod) console.debug(findKey(methods, (id) => id === responseActionId) + ` 👇 [${responseActionId}]`, responseProto.toObject());

      let wrapper!: RequestWrapper;

      if (responseProto.getResponse()) {
        setServerTime(responseProto.getResponse()!.getTimestamp());

        if (responseProto.getResponse()!.getId()) {
          wrapper = Api.instance.getRequestWrapper(responseProto.getResponse()!.getId());
          //
          if (!wrapper) {
            return;
          }
        }
      }
      if (responseActionId === 0 && responseProto instanceof ErrorResponse) {
        if (wrapper) {
          wrapper.reject(new ServerError(responseProto, wrapper.actionId));
        } else new HandleErrorResponse(responseProto).handle();
      } else {
        if (wrapper) {
          wrapper.resolve(responseProto);
        }
        this.__runHandler(responseActionId, responseProto, wrapper);
      }
    } else {
      // console.warn(`Unsupported method ${responseActionId}`);
    }
  }

  /**
   * @param {number} responseActionId
   * @param {MessageResponse} responseProto
   * @param {RequestWrapper} wrapper
   */
  __runHandler(responseActionId: number, responseProto: MessageResponse, wrapper?: RequestWrapper) {
    const handlerClass = handlerTable[responseActionId];
    if (handlerClass) {
      const handler = new handlerClass(responseProto, wrapper ? wrapper.proto : undefined);
      handler.handle();
    } else {
      // console.warn("Handler class not found " + responseActionId);
    }
  }

  close(option?: { forceClose?: boolean; keepAlive?: boolean }) {
    console.log("#SDocket Close", this?._socket);
    if (option?.keepAlive !== undefined) this.keepAlive = option?.keepAlive;
    if (this?._socket) {
      this._socket.close();
    }
    if (option?.forceClose && this?._socket?.readyState === this?._socket?.CLOSING) {
      this?._onClose();
    }
  }

  destroy() {
    if (this?._socket) {
      this._socket.close(1012);
    }
  }

  async _onClose() {
    if (this._socket?.readyState !== this._socket?.OPEN) {
      // console.log("#Timeout-_onClose1");
      if (this._keepAlive) {
        store.dispatch(setConnectionStatus(ConnectionStatusType.DISCONNECTED));
      }
      await msSleep(toNumber(process.env.REACT_APP_WEBSOCKET_RECONNECT_INTERVAL!));
      await this._init();
    }
  }

  packAndSendRequest(wrapper: RequestWrapper) {
    if (!this._socket || (!this._secure && !wrapper.options.insecure)) {
      throw new ClientError(`Insecure connection cannot send action #${wrapper.actionId}`);
    }
    let pack = this._pack(wrapper.actionId, wrapper.proto);
    if (this._secure) {
      pack = this._encrypt(pack);
    }
    this.sendRequest(pack);
    wrapper.startTimeout();
  }

  /**
   * @param {RequestWrapper} wrapper
   */

  sendRequest(pack: any) {
    if (this._socket?.readyState) this._socket?.send(pack);
  }

  /**
   * Pack
   * @param {number} actionId
   * @param {object} proto
   * @returns {Uint8Array}
   * @private
   */
  _pack(actionId: number, proto: Message): Uint8Array {
    const actionIdData = new DataView(new ArrayBuffer(2), 0);
    actionIdData.setUint16(0, actionId, true);
    const payload = proto.serializeBinary();
    const pack = new Uint8Array(2 + payload.byteLength);
    pack.set(new Uint8Array(actionIdData.buffer));
    pack.set(payload, 2);
    return pack;
  }

  /**
   * Encrypt
   * @param {ArrayBuffer | Uint8Array} data
   * @param {string} target
   * @returns {Uint8Array}
   * @private
   */
  _encrypt(data: ArrayBuffer | Uint8Array, target?: "upload"): Uint8Array {
    if (!this._symmetricIvSize || !this._symmetricMethod || !this._symmetricKey) {
      throw new ClientError("Invalid Symmetric Algorithm");
    }

    const iv = forge.random.getBytesSync(this._symmetricIvSize);

    //@ts-ignore
    const cipher = forge.cipher.createCipher(`${this._symmetricMethod[0]}-${this._symmetricMethod[2]}` as forge.cipher.Algorithm, this._symmetricKey);
    cipher.start({
      iv: iv,
      tagLength: target === "upload" ? 256 : 128,
    });

    const length = data.byteLength;
    const chunkSize = 1024 * 64;
    let index = 0;
    let encrypted = "";
    do {
      encrypted += cipher.output.getBytes();
      const buf = forge.util.createBuffer(data.slice(index, index + chunkSize));
      cipher.update(buf);
      index += chunkSize;
    } while (index < length);

    if (!cipher.finish()) {
      throw new Error();
    }
    encrypted += cipher.output.getBytes();
    return stringToUint8Array(iv + encrypted);
  }

  /**
   * Decrypt
   * @param {ArrayBuffer} data
   * @param {string} target
   * @returns {Uint8Array}
   * @private
   */
  _decrypt(data: ArrayBuffer, target?: "download", symmetricKey?: string): Uint8Array {
    if (!this._symmetricIvSize || !this._symmetricMethod || !this._symmetricKey) {
      throw new ClientError("Invalid Symmetric Algorithm");
    }

    const encryptedBytes = data.slice(this._symmetricIvSize);
    //@ts-ignore
    const decipher = forge.cipher.createDecipher(`${this._symmetricMethod[0]}-${this._symmetricMethod[2]}` as forge.cipher.Algorithm, symmetricKey || this._symmetricKey);
    decipher.start({
      iv: arrayBufferToString(data.slice(0, this._symmetricIvSize)),
      tagLength: target === "download" ? 256 : 128,
    });

    const length = encryptedBytes.byteLength;
    const chunkSize = 1024 * 64;
    let index = 0;
    let decrypted = "";
    do {
      decrypted += decipher.output.getBytes();
      const buf = forge.util.createBuffer(encryptedBytes.slice(index, index + chunkSize));
      decipher.update(buf);
      index += chunkSize;
    } while (index < length);
    decipher.finish();
    decrypted += decipher.output.getBytes();
    return stringToUint8Array(decrypted);
  }
}
